import { useState } from "react";
import { CheckCircle2, ChevronDown, ChevronUp } from "lucide-react";
import type { AHTPlannedAppointment } from "@/lib/pharmacyFirst/utils/generateAHTAppointments";

interface AHTTreatmentPlanCardProps {
  plan: {
    resolvedDrugId: string;
    currentDose: string;
    nextSupplyDueDate: string;
    plannedAppointmentDetails: AHTPlannedAppointment[];
  };
  isDarkMode?: boolean;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

function formatDate(isoDate: string): string {
  const date = new Date(isoDate + "T00:00:00Z");
  const day = date.getUTCDate();
  const month = MONTHS[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  if (month === undefined) return isoDate;
  return `${day} ${month} ${year}`;
}

function AppointmentRow({ appointment }: { appointment: AHTPlannedAppointment }) {
  return (
    <div className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">{formatDate(appointment.date)}</p>
          <p className="text-sm mt-0.5">{appointment.serviceType}</p>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {appointment.requiresVideoOrF2F && (
              <span className="inline-flex items-center rounded-full border border-transparent bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 px-2 py-0.5 text-xs font-semibold">
                Video / F2F required
              </span>
            )}
            {appointment.isTitrationAppointment && (
              <span className="inline-flex items-center rounded-full border border-transparent bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 px-2 py-0.5 text-xs font-semibold">
                Titration
              </span>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 shrink-0 pt-0.5">
          Questionnaire sent {appointment.preConsultationSendValue} days before
        </p>
      </div>
    </div>
  );
}

export function AHTTreatmentPlanCard({
  plan,
  isDarkMode = false,
}: AHTTreatmentPlanCardProps) {
  const [expanded, setExpanded] = useState(false);

  const drugLabel =
    plan.resolvedDrugId.charAt(0).toUpperCase() + plan.resolvedDrugId.slice(1);

  return (
    <div
      className={`rounded-lg border overflow-hidden ${
        isDarkMode ? "border-green-800 bg-green-950/40" : "border-green-200 bg-green-50"
      }`}
    >
      {/* Header */}
      <div
        className={`flex items-center gap-2 px-4 py-3 ${
          isDarkMode ? "bg-green-900/50" : "bg-green-100"
        }`}
      >
        <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
        <span
          className={`text-sm font-semibold ${
            isDarkMode ? "text-green-200" : "text-green-800"
          }`}
        >
          AHT Treatment Plan Created
        </span>
      </div>

      {/* Summary */}
      <div className="px-4 py-3 space-y-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}>
            {drugLabel}
          </span>
          <span className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            {plan.currentDose}
          </span>
        </div>
        <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
          Next supply: <span className="font-medium">{formatDate(plan.nextSupplyDueDate)}</span>
        </p>
        <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
          {plan.plannedAppointmentDetails.length} appointments scheduled
        </p>
      </div>

      {/* Toggle */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className={`w-full flex items-center justify-between px-4 py-2 text-xs font-medium border-t transition-colors ${
          isDarkMode
            ? "border-green-800 text-green-300 hover:bg-green-900/40"
            : "border-green-200 text-green-700 hover:bg-green-100"
        }`}
      >
        <span>{expanded ? "Hide appointments" : "Show appointments"}</span>
        {expanded ? (
          <ChevronUp className="w-3.5 h-3.5" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5" />
        )}
      </button>

      {/* Expanded appointment list */}
      {expanded && (
        <div
          className={`overflow-y-auto divide-y px-4 ${
            isDarkMode ? "divide-green-900" : "divide-green-100"
          }`}
          style={{ maxHeight: "320px" }}
        >
          {plan.plannedAppointmentDetails.map((appointment) => (
            <AppointmentRow key={appointment.id} appointment={appointment} />
          ))}
        </div>
      )}
    </div>
  );
}
